import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Globe2, MapPin, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useApp } from '../../context/AppContext'
import { countryName, poiActivity } from '../../lib/activityMap'
import { getAnalyticsInterest } from '../../lib/api'
import type { AnalyticsInterest, Answer, POI, Recommendation } from '../../lib/types'
import { pick } from '../../lib/utils'
import { ConfidenceBadge, DataTag } from '../Domain'
import { LeviPoiMap } from '../maps/LeviPoiMap'
import { Badge, Card, CardContent, CardHeader, CardTitle, NadiaLoader, Skeleton } from '../ui'

export function RecommendationCard({
  rec,
  rank,
  interest,
}: {
  rec: Recommendation
  rank: number
  interest?: AnalyticsInterest
}) {
  const { t } = useTranslation()
  const { locale, theme } = useApp()
  const { poi } = rec
  // Cruce POI <-> Google Analytics: la categoria de OSM del negocio mapea a una
  // categoria de actividad de GA, y de ahi sale la demanda web real.
  const activity = poiActivity(poi.category, poi.industry)
  const ga = interest?.activities.find((a) => a.category === activity)
  const topTraveler = (poi.traveler_types ?? [])[0]
  const topOrigin = (poi.traveler_origins ?? [])[0]
  const topCluster = (poi.clusters ?? [])[0]
  const topTopic = (poi.topics ?? [])[0]
  const highlightLabel = topCluster?.cluster ?? topTopic?.topic ?? null
  const profile = rec.profile
  // OpenStreetMap, no Google: el POI viene de Overpass y la ubicacion de OSM
  // es la que corresponde al dato. Poner un enlace a otro proveedor seria
  // inventar una atribucion que nadie reviso.
  //
  // `osm_id` llega como `node/253942053` o `way/12345` (tipo + id de Overpass),
  // no como una URL. Se parte por la barra en vez de adivinar el tipo con una
  // expresion regular: si Overpass devolviera `relation/...`, esta sigue
  // funcionando y la otra habria publicado un enlace roto.
  const osmUrl = poi.osm_id ? `https://www.openstreetmap.org/${poi.osm_id}` : null

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground font-mono text-xs">#{rank}</span>
              <CardTitle className="truncate">{poi.name}</CardTitle>
            </div>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {poi.industry}
              {poi.city ? ` · ${poi.city}` : ''}
              {poi.stars ? ` · ${poi.stars}★` : ''}
            </p>
          </div>
          <div className="shrink-0 text-right">
              <p className="text-muted-foreground text-[11px]">{t('what.scoreSimulated', 'Puntuación del modelo · simulada')}</p>
            <p
              className={`font-mono text-xl font-semibold ${
                rank === 1 ? 'text-primary' : ''
              } ${theme === 'light' ? '' : 'text-foreground'}`}
            >
              {rec.score.toFixed(2)}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-muted-foreground text-xs font-medium">{t('what.reason')}</p>
          <p className="mt-0.5 text-sm">{pick(rec.reason, locale)}</p>
        </div>

        {/* Cruce con Google Analytics: demanda web REAL del tipo de negocio */}
        {ga ? (
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-2.5 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-semibold text-sky-400">
                <Globe2 className="size-3.5" />
                {t('what.activityDemand', 'Demanda web real')}
              </span>
              <span className="font-mono">{Math.round(ga.share * 100)}%</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t('what.activityOf', 'de las visitas del sitio (Google Analytics) son de')}:{' '}
              <b>{ga.label}</b>
            </p>
            {ga.top_countries.length ? (
              <p className="text-[11px] text-muted-foreground">
                {t('what.topCountries', 'Países más interesados')}:{' '}
                {ga.top_countries
                  .slice(0, 4)
                  .map((c) => `${countryName(c.country, locale)} ${Math.round(c.share * 100)}%`)
                  .join(' · ')}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Detalle del Data Pack (simulado): perfil del negocio */}
        {topTraveler || topOrigin || highlightLabel || profile ? (
          <div className="rounded-lg border border-border/60 bg-muted/20 p-2.5 space-y-1">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-400">
              <Sparkles className="size-3" />
              {t('what.packProfile', 'Perfil del negocio (Data Pack · simulado)')}
            </span>
            <ul className="text-[11px] text-muted-foreground space-y-0.5">
              {topTraveler ? (
                <li>
                  {t('what.travelerType', 'Tipo de viajero')}: {topTraveler.type} (
                  {Math.round(topTraveler.percentage)}%)
                </li>
              ) : null}
              {topOrigin ? (
                <li>
                  {t('what.originMarket', 'Mercado de origen')}: {topOrigin.country} (
                  {Math.round(topOrigin.percentage)}%)
                </li>
              ) : null}
              {highlightLabel ? (
                <li>
                  {t('what.highlight', 'Punto fuerte')}: {highlightLabel}
                </li>
              ) : null}
              {profile?.services.length ? (
                <li>
                  {t('what.services', 'Servicios')}: {profile.services.join(', ')}
                </li>
              ) : null}
              {profile?.payments.length ? (
                <li>
                  {t('what.payments', 'Pagos')}: {profile.payments.join(', ')}
                </li>
              ) : null}
              {profile && (profile.cuisine.length || profile.atmosphere.length) ? (
                <li>
                  {t('what.cuisineAmbience', 'Cocina/ambiente')}:{' '}
                  {[...profile.cuisine, ...profile.atmosphere].join(', ')}
                </li>
              ) : null}
              {profile?.busy_times.length ? (
                <li>
                  {t('what.busyTimes', 'Mayor afluencia')}: {profile.busy_times.join(' · ')}
                </li>
              ) : null}
              {profile?.destination_price ? (
                <li>
                  {t('what.priceRef', 'Referencia de destino')}:{' '}
                  {t('what.median', 'precio medio')} ~{Math.round(profile.destination_price)} €
                  {profile.destination_occupancy != null
                    ? ` · ${t('what.occupancy', 'ocupación')} ${Math.round(profile.destination_occupancy)}%`
                    : ''}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {rec.best_window.length ? (
          <div className="flex flex-wrap gap-1.5">
            {rec.best_window.slice(0, 3).map((p) => (
              <Badge key={p.period} variant="muted" className="font-mono text-[10px]">
                {p.period} · {p.value.toFixed(2)}
              </Badge>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-1.5">
          <ConfidenceBadge level={rec.confidence} />
          {rec.warnings.includes('simulated') ? (
            <Badge variant="warning">{t('common.simulated')}</Badge>
          ) : null}
          <DataTag isEstimate={rec.warnings.includes('simulated')} datasetIds={rec.provenance_dataset_ids} />
        </div>

        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
          {poi.street_address ? (
            <span className="flex items-center gap-1">
              <MapPin className="size-3" />
              {poi.street_address}
            </span>
          ) : null}
          {poi.website ? (
            <a
              href={poi.website}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary flex items-center gap-1 hover:underline"
            >
              <ExternalLink className="size-3" />
              {t('what.website')}
            </a>
          ) : (
            <span>{t('what.noWebsite')}</span>
          )}
        </div>

        {osmUrl ? (
          <a
            href={osmUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-muted-foreground inline-flex items-center gap-1 text-[11px] hover:underline"
          >
            <MapPin className="size-3" />
            {t('what.viewOnMap')}
          </a>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function WhatPanel({ answer, loading }: { answer?: Answer; loading: boolean }) {
  const { t } = useTranslation()
  const { locale } = useApp()
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null)

  const interest = useQuery({
    queryKey: ['analytics-interest', locale],
    queryFn: () => getAnalyticsInterest(locale, 30),
    staleTime: 5 * 60_000,
  })

  if (loading) {
    return (
      <div className="space-y-4">
        <NadiaLoader
          minHeight="min-h-[280px]"
          message={t('loading.loadingPois', 'Analizando catálogo de empresas y distribución espacial...')}
          subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    )
  }
  if (!answer) return null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {answer.headline[locale] ?? answer.headline.en}
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">{t('question.what.help')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ConfidenceBadge level={answer.confidence} score={answer.confidence_score} showScore />
          {answer.recommendations.some((r) => r.warnings.includes('simulated')) ? (
            <Badge variant="warning">{t('common.simulated')}</Badge>
          ) : null}
        </div>
      </div>

      {answer.recommendations.length > 0 ? (
        <LeviPoiMap
          recommendations={answer.recommendations}
          selectedPoiId={selectedPoiId}
          onSelectPoi={(poi: POI) => setSelectedPoiId(poi.poi_id)}
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {answer.recommendations.map((rec, i) => (
          <div
            key={rec.poi.poi_id}
            onClick={() => setSelectedPoiId(rec.poi.poi_id)}
            className={`cursor-pointer transition-all ${
              selectedPoiId === rec.poi.poi_id ? 'ring-2 ring-primary rounded-xl' : ''
            }`}
          >
            <RecommendationCard rec={rec} rank={i + 1} interest={interest.data} />
          </div>
        ))}
      </div>

      {answer.recommendations.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('common.noData')}</p>
      ) : null}
    </div>
  )
}
